import { forwardRef } from 'react';
import { Controller, Control, ControllerProps, FieldValues, Path, RegisterOptions } from 'react-hook-form';
import { withCleanTextRule } from '@/utils/sanitize';
import { shouldUseRTL } from '@/utils/rtl';

// Only free-text types get the clean-text check; email, url, password, number etc. need symbols.
const TEXT_INPUT_TYPES = new Set([undefined, 'text', 'search']);

interface InputProps<T extends FieldValues> extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'name'> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  error?: string;
  required?: boolean;
  helperText?: string;
  rules?: RegisterOptions;
  /** VAPT #12/#13 - set true for fields that legitimately need symbols (URLs, IDs, code). */
  allowSpecialChars?: boolean;
  /** Text inputs switch to right-to-left for Arabic etc.; set true to keep left-to-right. */
  disableRTL?: boolean;
}

function InputComponent<T extends FieldValues>(
  { name, control, label, error, required, helperText, className = '', rules, allowSpecialChars = false, disableRTL = false, ...rest }: InputProps<T>,
  ref: any
) {
  const is_text_input = TEXT_INPUT_TYPES.has(rest.type);
  const check_clean_text = !allowSpecialChars && is_text_input;

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <Controller
        name={name}
        control={control}
        rules={withCleanTextRule(rules, check_clean_text) as unknown as ControllerProps<T, Path<T>>['rules']}
        render={({ field, fieldState }) => {
          // Fall back to the field's own error so a failed check is shown even if the page passes no `error`.
          const message = error ?? fieldState.error?.message;
          // Same detection as RTLInput: >= 30% right-to-left characters flips direction
          const is_rtl = !disableRTL && is_text_input && typeof field.value === 'string' && shouldUseRTL(field.value, 30);
          return (
            <>
              <input
                {...rest}
                {...field}
                ref={ref}
                id={name}
                dir={is_rtl ? 'rtl' : rest.dir}
                style={is_rtl ? { ...rest.style, direction: 'rtl', textAlign: 'right' } : rest.style}
                className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                  message ? 'border-red-500 focus:ring-red-500' : 'border-gray-200'
                } ${className}`}
              />
              {message && <p className="text-red-500 text-xs mt-1">{message}</p>}
              {helperText && !message && <p className="text-gray-500 text-xs mt-1">{helperText}</p>}
            </>
          );
        }}
      />
    </div>
  );
}

export const Input = forwardRef(InputComponent) as <T extends FieldValues>(
  props: InputProps<T> & { ref?: any }
) => ReturnType<typeof InputComponent>;
